'use client';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  return (
    <section className="relative min-h-[90vh] bg-gray-900 text-white overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-gray-900 to-secondary/20" />
      
      <div className="relative z-10 max-w-container mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Content - 60% */}
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
              <span className="text-xs font-semibold uppercase tracking-widest text-white">Est. July 26, 2001</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold leading-tight tracking-tight mb-6">
              Your Trusted Wholesale Supplier
              <span className="block text-3xl md:text-4xl lg:text-5xl text-gray-300 font-light mt-4">Supplies Distributors SA</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed mb-10 max-w-2xl">
              We deliver premium cooking oils, wood pellets, chicken feed, fertilizers, and iron scraps to businesses worldwide. Quality you can trust. Service you can count on.
            </p>

            {/* CTA Group */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                href="/contact"
                className="btn-scale inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold hover:bg-primary/90 shadow-lg">
                
                Request Quote
                <Icon name="ArrowUpRightIcon" size={20} className="arrow-shift" />
              </Link>
              <Link
                href="/products"
                className="btn-scale inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white hover:text-gray-900">
                
                View Products
                <Icon name="ArrowRightIcon" size={20} className="arrow-shift" />
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-6 max-w-xl">
              <div className="text-center">
                <div className="text-3xl font-bold font-serif text-white mb-1">5,000+</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Global Partners</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold font-serif text-white mb-1">25+</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Years in Business</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold font-serif text-white mb-1">ISO</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Certified Quality</div>
              </div>
            </div>

            {/* Country Flags */}
            <div className="flex items-center gap-4 mt-8">
              <span className="text-xs text-gray-400 uppercase tracking-wider">Guaranteed Partners In:</span>
              <div className="flex items-center gap-2">
                <span className="text-2xl" title="Thailand">🇹🇭</span>
                <span className="text-2xl" title="Brazil">🇧🇷</span>
                <span className="text-2xl" title="Morocco">🇲🇦</span>
                <span className="text-2xl" title="Netherlands">🇳🇱</span>
                <span className="text-2xl" title="Germany">🇩🇪</span>
                <span className="text-xs text-gray-400">and more</span>
              </div>
            </div>
          </div>

          {/* Right Content - 40% Floating Product Cards */}
          <div className="lg:col-span-5 relative h-[500px] reveal">
            {/* Card 1: Cooking Oil - Top Left */}
            <div className="absolute top-0 left-0 w-48 transform -rotate-6 hover:rotate-0 transition-transform duration-500">
              <div className="bg-white/10 backdrop-blur-md rounded-organic p-4 border border-white/20 shadow-2xl hover-lift">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1479c9065-1767663906327.png"
                  alt="Premium cooking oil bottle with golden color"
                  className="w-full h-32 object-cover rounded-lg mb-3" />
                
                <div className="text-sm font-semibold text-white">Cooking Oils</div>
                <div className="text-xs text-gray-300">Premium Quality</div>
              </div>
            </div>

            {/* Card 2: Wood Pellets - Center Right */}
            <div className="absolute top-20 right-0 w-56 transform rotate-3 hover:rotate-0 transition-transform duration-500">
              <div className="bg-white/10 backdrop-blur-md rounded-organic p-4 border border-white/20 shadow-2xl hover-lift scale-110">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1d6154573-1766287103429.png"
                  alt="Wood pellets in burlap sack showing sustainable biomass fuel"
                  className="w-full h-36 object-cover rounded-lg mb-3" />
                
                <div className="text-sm font-semibold text-white">Wood Pellets</div>
                <div className="text-xs text-gray-300">Sustainable Energy</div>
              </div>
            </div>

            {/* Card 3: Chicken Feed - Bottom Left */}
            <div className="absolute bottom-0 left-12 w-52 transform rotate-6 hover:rotate-0 transition-transform duration-500">
              <div className="bg-white/10 backdrop-blur-md rounded-organic p-4 border border-white/20 shadow-2xl hover-lift">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_17ece5f38-1765513525216.png"
                  alt="Chicken feed grains in bowl showing poultry nutrition"
                  className="w-full h-32 object-cover rounded-lg mb-3" />
                
                <div className="text-sm font-semibold text-white">Chicken Feed</div>
                <div className="text-xs text-gray-300">Poultry Nutrition</div>
              </div>
            </div>

            {/* Gradient Accent */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/30 to-accent/30 rounded-full blur-3xl opacity-50 pointer-events-none" />
          </div>
        </div>
      </div>
    </section>);

}